import React, { useState } from 'react';
import { Settings, Calendar, Wrench, Users, BarChart3, Home, LogOut, TrendingUp, UserCircle, Wind } from 'lucide-react';
import AdminServices from './AdminServices';
import AdminAppointments from './AdminAppointments';
import AdminSettings from './AdminSettings';
import AdminReports from './AdminReports';
import AdminCustomers from './AdminCustomers';
import AdminACUnitsAuto from './AdminACUnitsAuto';

// Import the dashboard overview component we created
const AdminDashboardOverview = ({ 
  services, 
  appointments, 
  coupons, 
  blockedDates,
  adminSettings 
}) => {
  // Calculate statistics
  const getStats = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
    // Total appointments (current year)
    const totalAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate.getFullYear() === now.getFullYear();
    }).length;
  
    // FIXED: Upcoming appointments (next 7 days) - return COUNT not array
    const upcomingAppointments = appointments.filter(apt => {
      // Use T00:00:00 to avoid timezone issues
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate >= today && aptDate <= nextWeek && apt.status === 'scheduled';
    });
  
    // FIXED: Today's appointments - return COUNT not array
    const todaysAppointments = appointments.filter(apt => {
      // Use T00:00:00 to avoid timezone issues
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate.toDateString() === today.toDateString() && apt.status === 'scheduled';
    });
  
    // FIXED: This month's appointments
    const thisMonthAppointments = appointments.filter(apt => {
      // Use T00:00:00 to avoid timezone issues
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate >= thisMonth && apt.status === 'completed';
    });
  
    // Revenue calculations - current year only
    const thisYearAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate.getFullYear() === now.getFullYear();
    });

    const totalRevenue = thisYearAppointments
      .filter(apt => apt.status === 'completed')
      .reduce((sum, apt) => sum + (apt.final_price || apt.service_price || 0), 0);
  
    const thisMonthRevenue = thisMonthAppointments
      .reduce((sum, apt) => sum + (apt.final_price || apt.service_price || 0), 0);
  
    return {
      totalAppointments,
      upcomingCount: upcomingAppointments.length, // FIXED: Return count, not array
      todaysCount: todaysAppointments.length,     // FIXED: Return count, not array
      thisMonthCount: thisMonthAppointments.length,
      totalRevenue,
      thisMonthRevenue,
      upcomingAppointments, // Keep arrays for detailed display if needed
      todaysAppointments
    };
};

  const stats = getStats();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Year's Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAppointments}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
              <p className="text-2xl font-bold text-green-600">{stats.todaysCount}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.thisMonthRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <span className="text-green-600 font-bold text-lg">$</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Next 7 Days</p>
              <p className="text-2xl font-bold text-purple-600">{stats.upcomingCount}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-xl shadow-lg p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold mb-2">Welcome to {adminSettings.business_name || 'AllTech LLC'}</h3>
            <p className="text-green-100">
              You have {stats.todaysCount} appointments today and {stats.upcomingCount} coming up this week.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-green-100">This Year's Revenue</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Business Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Active Services</span>
              <span className="font-semibold text-gray-800">{services.length}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Active Coupons</span>
              <span className="font-semibold text-gray-800">
                {coupons.filter(c => c.active).length}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Blocked Dates</span>
              <span className="font-semibold text-gray-800">{blockedDates.length}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Average Service Price</span>
              <span className="font-semibold text-gray-800">
                {services.length > 0 
                  ? formatCurrency(services.reduce((sum, s) => sum + s.price, 0) / services.length)
                  : formatCurrency(0)
                }
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Activity</h3>
          {appointments.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No appointments yet</p>
              <p className="text-sm text-gray-400">New appointments will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.slice(-3).reverse().map(appointment => (
                <div key={appointment.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-800">{appointment.customer_name}</h4>
                      <p className="text-sm text-gray-600">{appointment.service_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(appointment.appointment_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      {formatCurrency(appointment.final_price || appointment.service_price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminLayout = ({
  currentPage,
  setCurrentPage,
  services,
  setServices,
  appointments,
  customers,
  setCustomers,
  coupons,
  setCoupons,
  blockedDates,
  setBlockedDates,
  adminSettings,
  setAdminSettings,
  isAdmin,
  setIsAdmin,
  // Database API functions
  createService,
  updateService,
  deleteService,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  saveAdminSettings,
  createBlockedDate,
  deleteBlockedDate,
  loadAppointments,
  loadBlockedDates,
  // FIXED: Added missing coupon database functions
  createCoupon,
  updateCoupon,
  deleteCoupon,
  // Customer database functions
  createCustomer,
  updateCustomer,
  deleteCustomer,
  loadCustomers,
  // Bundle functions
  bundles,
  setBundles,
  createBundle,
  updateBundle,
  deleteBundle
}) => {
  const [activeAdminPage, setActiveAdminPage] = useState('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, description: 'Overview & Analytics' },
    { id: 'appointments', label: 'Appointments', icon: Users, description: 'Manage Bookings' },
    { id: 'customers', label: 'Customers', icon: UserCircle, description: 'Customer Database' },
    { id: 'ac-units', label: 'AC Units', icon: Wind, description: 'AC Unit Database' },
    { id: 'reports', label: 'Reports', icon: TrendingUp, description: 'Revenue & Analytics' },
    { id: 'services', label: 'Services', icon: Wrench, description: 'Pricing & Offerings' },
    { id: 'settings', label: 'Settings', icon: Settings, description: 'Business Config' }
  ];

  const renderContent = () => {
    const sharedProps = {
      services,
      setServices,
      appointments,
      customers,
      setCustomers,
      coupons,
      setCoupons,
      blockedDates,
      setBlockedDates,
      adminSettings,
      setAdminSettings,
      createService,
      updateService,
      deleteService,
      createAppointment,
      updateAppointment,
      deleteAppointment,
      saveAdminSettings,
      createBlockedDate,
      deleteBlockedDate,
      loadAppointments,
      loadBlockedDates,
      // FIXED: Added missing coupon functions to shared props
      createCoupon,
      updateCoupon,
      deleteCoupon,
      // Customer functions
      createCustomer,
      updateCustomer,
      deleteCustomer,
      loadCustomers,
      // Bundle functions
      bundles,
      setBundles,
      createBundle,
      updateBundle,
      deleteBundle
    };


    switch (activeAdminPage) {
      case 'dashboard':
        return <AdminDashboardOverview {...sharedProps} />;
      case 'appointments':
        return <AdminAppointments {...sharedProps} />;
      case 'customers':
        return <AdminCustomers {...sharedProps} />;
      case 'ac-units':
        return <AdminACUnitsAuto {...sharedProps} />;
      case 'reports':
        return <AdminReports {...sharedProps} />;
      case 'services':
        return <AdminServices {...sharedProps} />;
      case 'settings':
        return <AdminSettings {...sharedProps} />;
      default:
        return <AdminDashboardOverview {...sharedProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-green-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/shared/images/Alltech-Logo.png" 
                alt="Alltech LLC Logo" 
                className="h-10 w-auto"
                onError={(e) => {
                  // Hide if logo doesn't load
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold">{adminSettings.business_name || 'Alltech LLC'}</h1>
                <p className="text-green-100 text-sm">Admin Dashboard v3.0 - Payment Fix Active ✅</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-300 rounded-full"></div>
                  <span>System Online</span>
                </div>
              </div>
              <button
                onClick={() => {
                  // Clear authentication from localStorage
                  localStorage.removeItem('isAdminAuthenticated');
                  localStorage.removeItem('adminLoginTime');

                  setIsAdmin(false);
                  setCurrentPage('welcome');
                }}
                className="bg-green-700 hover:bg-green-800 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Navigation</h3>
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeAdminPage === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveAdminPage(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                        isActive
                          ? 'bg-green-100 text-green-800 border-l-4 border-green-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{item.label}</div>
                        <div className="text-xs opacity-75">{item.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Services</span>
                    <span className="font-medium">{services.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Appointments</span>
                    <span className="font-medium">{appointments.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Blocked Dates</span>
                    <span className="font-medium">{blockedDates.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Coupons</span>
                    <span className="font-medium">{coupons.filter(c => c.active).length}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 capitalize">
                {activeAdminPage === 'dashboard' ? 'Dashboard Overview' : activeAdminPage}
              </h2>
              <p className="text-gray-600 mt-1">
                {menuItems.find(item => item.id === activeAdminPage)?.description}
              </p>
            </div>
            
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;